import type { Permission } from '../access/role';
import { daysBefore } from '../time/dateRange';

/**
 * Deleting a case, with approval, for thirty days.
 *
 * A duplicate opened at intake, a case against the wrong dentist, a case the
 * dentist cancelled the same afternoon: none of them could be removed, and all
 * of them were worked around by holding them, which is what hold is for and this
 * is not.
 *
 * Two permissions, never a role name. A holder of `approveDeletion` deletes
 * outright; a holder of only `deleteCase` asks, and the case is deleted when
 * somebody else approves. The matrix seeds both to Management so the client's
 * sentence is true on day one, and the lab can move either without a deploy.
 *
 * Not an order state. `orderState` is computed from readiness, hold, payment and
 * dispatch, and a deletion is orthogonal to all four, so it is a block of its
 * own beside hold and dispatch.
 */

export const DELETION_STATUSES = ['none', 'pending', 'deleted'] as const;
export type DeletionStatus = (typeof DELETION_STATUSES)[number];

export const DELETION_REASON_MAX = 200;

/** A deleted case is kept this many lab days, then purged for good. */
export const PURGE_AFTER_DAYS = 30;

/** The refusals, worded once for the mock server, the screens and the tests. */
export const DELETION_REFUSAL = {
  alreadyDeleting: 'This case already has a deletion on it.',
  ownApproval: 'A deletion has to be approved by somebody else.',
  nothingPending: 'There is no deletion waiting on this case.',
  notDeleted: 'This case has not been deleted.',
  dispatchPending: 'A deletion is waiting for approval on this case.',
  reasonRequired: 'Say why this case is being deleted',
  reasonTooLong: `Keep the reason to ${DELETION_REASON_MAX} characters`,
  // The Deleted tab's list, asked for without either permission. The request
  // of 5 October 2026 (third), section 10.
  listNotAllowed: 'You do not have permission to see deleted cases.',
} as const;

/** The deletion block on a job order. Every field but `status` is null while there is none. */
export interface Deletion {
  status: DeletionStatus;
  reason: string | null;
  requestedByUserId: string | null;
  requestedByUserName: string | null;
  requestedAt: string | null;
  decidedByUserId: string | null;
  decidedByUserName: string | null;
  decidedAt: string | null;
  /** When the case actually left the floor. Null while a request is pending. */
  deletedAt: string | null;
  /** The lab day it is purged, thirty lab days after `deletedAt`. Null until deleted. */
  purgesAt: string | null;
}

export const NO_DELETION: Deletion = {
  status: 'none',
  reason: null,
  requestedByUserId: null,
  requestedByUserName: null,
  requestedAt: null,
  decidedByUserId: null,
  decidedByUserName: null,
  decidedAt: null,
  deletedAt: null,
  purgesAt: null,
};

/**
 * The lab day a case deleted on `deletedDay` is purged. Days in Dubai, so a case
 * deleted at two in the morning there purges thirty Dubai days later rather than
 * a day early.
 */
export function purgeDay(deletedDay: string): string {
  return daysBefore(deletedDay, -PURGE_AFTER_DAYS);
}

/** Whole days from today until the purge: 0 on the day itself, never below it. */
export function daysUntilPurge(purgesAt: string, today: string): number {
  const millis = Date.parse(`${purgesAt}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`);
  return Math.max(0, Math.round(millis / 86_400_000));
}

/**
 * Whether a caller may read the deleted cases and the deletions waiting: whoever
 * may delete a case or decide a deletion. The archive screen has always shown
 * its Deleted tab by this; since the request of 5 October 2026 (third),
 * section 10, the server refuses the list by it too, so the tab is blocked and
 * not only hidden.
 */
export function maySeeDeleted(permissions: readonly Permission[]): boolean {
  return permissions.includes('deleteCase') || permissions.includes('approveDeletion');
}

/**
 * What deleting a case does for this caller. It takes `deleteCase` to delete at
 * all; holding `approveDeletion` as well deletes outright, and without it the
 * deletion waits for somebody who does. The dialog says which before anybody
 * presses anything.
 */
export function deletionOutcome(permissions: readonly Permission[]): 'delete' | 'request' | null {
  if (!permissions.includes('deleteCase')) return null;
  return permissions.includes('approveDeletion') ? 'delete' : 'request';
}

/**
 * What a caller may do about a deletion waiting for approval.
 *
 * Nobody approves their own request, not even a holder of both permissions:
 * another holder must. Any other approver may approve or refuse it. The person
 * who asked withdraws it, which is the same act as a refusal on the server and is
 * offered under the word that fits who is doing it.
 */
export function pendingDeletionActions(
  deletion: Pick<Deletion, 'status' | 'requestedByUserId'>,
  callerId: string,
  permissions: readonly Permission[],
): { approve: boolean; refuse: boolean; withdraw: boolean } {
  if (deletion.status !== 'pending') return { approve: false, refuse: false, withdraw: false };

  const own = deletion.requestedByUserId === callerId;
  const approver = permissions.includes('approveDeletion');
  return {
    approve: approver && !own,
    refuse: approver && !own,
    withdraw: own && (approver || permissions.includes('deleteCase')),
  };
}
